'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  CONTACT_SYNC,
  CONTACT_SYNC_STATUS,
  type IContactSyncData,
  type IContactSyncStatusData,
  type IContactSyncStatusVar,
  type IContactSyncVar,
} from '@quesmed/types/resolvers/mutation/admin';
import { motion } from 'framer-motion';
import { useMemo } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useDisclosure } from '@/core/hooks';
import { ErrorFeedback, Heading, SuccessDataFeedback } from '@/core/molecules';
import { Alert, AnimatedComponent, Button, Confirm, cn, FormTextArea } from '@/core/ui';
import { useMutation } from '@/libs';

const SyncContact = ({ className }: { className?: string }) => {
  const schema = z.object({
    emails: z.string().refine((val) => val.split(',').every((id) => z.email().safeParse(id.trim()).success), {
      message: 'Input must only contain valid emails separated by commas.',
    }),
  });

  type ISchemaInput = z.input<typeof schema>;

  const {
    control,
    getValues,
    handleSubmit,
    formState: { isValid },
  } = useForm<ISchemaInput>({
    mode: 'onChange',
    resolver: zodResolver(schema),
  });
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [contactSync, { loading, data }] = useMutation<IContactSyncData, IContactSyncVar>(CONTACT_SYNC);
  const finalData = useMemo(() => data?.admin?.contactSync, [data?.admin?.contactSync]);

  const onSubmit: SubmitHandler<ISchemaInput> = async () => {
    onOpen();
  };

  const onConfirm = async () => {
    const input = getValues();
    const emails = input.emails
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    await contactSync({ variables: { emails } });
  };

  return (
    <>
      <Confirm
        onConfirm={onConfirm}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        title='Sync Contacts'
        message='Are you sure want to proceed?'
      />
      <div className={cn('flex flex-col space-y-2 justify-center w-full items-center', className)}>
        <div className='bg-background rounded-lg shadow-md w-full z-[2]'>
          <Heading title='Sync SendGrid Contacts' />
          <form onSubmit={handleSubmit(onSubmit)} className='p-6 md:p-10'>
            <Alert
              type='warn'
              className='mb-8'
              message='Please read below before proceeding!'
              list={[
                'Update may take a while to get updated in sendgrid as its a job/queue process!',
                'Check Job status in right section',
                'Please do not perform this task very often as we already have mostly synced contacts',
              ]}
            />
            <div className='grid grid-cols-2 items-start gap-y-6 md:gap-y-10 gap-x-8'>
              <FormTextArea
                minRows={4}
                description='Only those users will be synced, Leave empty if applicable for all'
                control={control}
                name='emails'
                labelPlacement='outside'
                placeholder='one@gmail.com, two@gmail.com'
                className='rounded col-span-2'
                variant='faded'
                label='Enter usernames (comma separated) (Optional)'
              />
            </div>
            <div className='flex justify-center mt-10'>
              <Button
                isDisabled={!isValid}
                isLoading={loading}
                className='w-full max-w-xl bg-primary'
                type='submit'
                radius='sm'
                size='lg'
              >
                Submit
              </Button>
            </div>
          </form>
        </div>
        <AnimatedComponent>
          {finalData && (
            <motion.div
              initial={{ y: -160 }}
              animate={{ y: 0 }}
              exit={{ y: -160 }}
              transition={{ type: 'spring' }}
              className={cn(
                'flex flex-col space-y-4 justify-center items-center',
                'w-full py-5',
                'bg-background rounded-lg shadow-md ',
              )}
            >
              {finalData?.status === 200 ? (
                <SuccessDataFeedback message={finalData?.message} response={finalData} />
              ) : (
                <ErrorFeedback response={finalData} />
              )}
            </motion.div>
          )}
        </AnimatedComponent>
      </div>
    </>
  );
};

const SyncStatus = ({ className }: { className?: string }) => {
  const schema = z.object({
    jobIds: z
      .string()
      .min(1, 'Job Ids are required')
      // .transform((val) => {
      //   const uuids = val
      //     .split(',')
      //     .map((id) => id.trim())
      //     .filter(Boolean);
      //   return uuids;
      // })
      .refine(
        (uuids) => (uuids ? uuids.split(',').every((id) => z.uuid().safeParse(id.trim()).success) : true),
        {
          message: 'JobIds must only contain valid UUIDs separated by commas.',
        },
      ),
  });

  type ISchema = z.infer<typeof schema>;

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<ISchema>({
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  const [contactSyncStatus, { loading, data }] = useMutation<IContactSyncStatusData, IContactSyncStatusVar>(
    CONTACT_SYNC_STATUS,
  );

  const finalData = useMemo(() => data?.admin?.contactSyncStatus, [data?.admin?.contactSyncStatus]);

  const onSubmit: SubmitHandler<ISchema> = async (input) => {
    const jobIds = input.jobIds
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    await contactSyncStatus({ variables: { jobIds } });
  };

  return (
    <div className={cn('flex flex-col space-y-2 justify-center w-full items-center', className)}>
      <div className='bg-background rounded-lg shadow-md w-full z-[2]'>
        <Heading title='Check Sendgrid Job Status' />

        <form onSubmit={handleSubmit(onSubmit)} className='p-4 md:p-6'>
          <div className='grid grid-cols-2'>
            <FormTextArea
              isRequired={true}
              control={control}
              description='Enter comma separated JobIds'
              name='jobIds'
              labelPlacement='outside'
              placeholder='04fe9191-24da-47bc-80d5-61f5089337ab, 3add7061-564f-4bf6-9cde-a1513cd404cc'
              className='rounded col-span-2'
              variant='faded'
              label='Job Ids'
            />
          </div>
          <div className='flex justify-center mt-10'>
            <Button
              isDisabled={!isValid}
              isLoading={loading}
              className='w-full bg-primary'
              type='submit'
              radius='sm'
              size='md'
            >
              Submit
            </Button>
          </div>
        </form>
      </div>
      <AnimatedComponent>
        {finalData && (
          <motion.div
            initial={{ y: -160 }}
            animate={{ y: 0 }}
            exit={{ y: -160 }}
            transition={{ type: 'spring' }}
            className={cn(
              'flex flex-col space-y-4 justify-center items-center',
              'w-full py-5 px-8',
              'bg-background rounded-lg shadow-md ',
            )}
          >
            {finalData?.status === 200 ? (
              <SuccessDataFeedback message={finalData?.message} response={finalData} />
            ) : (
              <ErrorFeedback response={finalData} />
            )}
          </motion.div>
        )}
      </AnimatedComponent>
    </div>
  );
};

const SendgridContact = () => {
  return (
    <div className='flex w-full justify-center h-full'>
      {/* <Alert
        type='warn'
        className='mb-6 md:mb-10'
        message='It wont function and will throw an error until it is bound to the development environment!'
      /> */}
      <div className='grid grid-cols-6 items-start gap-8 container'>
        <SyncContact className='col-span-6 xl:col-span-4' />
        <SyncStatus className='col-span-6 xl:col-span-2' />
      </div>
    </div>
  );
};

export default SendgridContact;
